import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { FileDown, Search, Filter } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BRAND_ORDER = ["all", "maltora", "onecola", "onefresh", "onespring", "voltx"];
const DOC_ICONS = {
  "catalogue": "📘", "tech-data": "📊", "specification": "📐",
  "nutrition": "🥤", "export-catalogue": "🌍", "company-profile": "🏢",
  "distributor-brochure": "🤝",
};

export default function Downloads() {
  const [library, setLibrary] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/downloads/library"),
      api.get("/downloads"),
    ]).then(([lib, leg]) => {
      setLibrary(lib.data.items || []);
      setLegacy(leg.data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let items = library;
    if (filter !== "all") items = items.filter((i) => i.brand_slug === filter);
    if (q.trim()) {
      const s = q.toLowerCase();
      items = items.filter((i) =>
        i.title.toLowerCase().includes(s) ||
        i.brand_name.toLowerCase().includes(s) ||
        i.doc_type.toLowerCase().includes(s)
      );
    }
    // sort: 'all' brand first, then alphabetical by brand + doc order
    const brandRank = (slug) => BRAND_ORDER.indexOf(slug);
    return [...items].sort((a, b) => brandRank(a.brand_slug) - brandRank(b.brand_slug) || a.doc_type.localeCompare(b.doc_type));
  }, [library, filter, q]);

  return (
    <div data-testid="downloads-page">
      <section className="pt-24 pb-14 bg-neutral-950 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="eyebrow text-gold-400 mb-4">Downloads Library</div>
          <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter">Product catalogues, spec sheets &amp; more.</h1>
          <p className="mt-4 text-neutral-300 max-w-2xl">
            Professionally-designed PDFs generated live from our master product database — always up to date with every SKU,
            format and specification. Download the version you need for consumers, distributors or investors.
          </p>
        </div>
      </section>

      <section className="py-10 bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-6 flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search catalogues, spec sheets, nutrition…"
              className="w-full pl-10 pr-4 py-3 rounded-full bg-muted/40 border border-border focus:border-gold-400 outline-none text-sm"
              data-testid="downloads-search"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground" />
            {BRAND_ORDER.map((slug) => {
              const label = slug === "all" ? "All" : slug.charAt(0).toUpperCase() + slug.slice(1);
              return (
                <button
                  key={slug} onClick={() => setFilter(slug)}
                  className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold border transition-colors ${
                    filter === slug
                      ? "bg-gold-400 border-gold-400 text-neutral-950"
                      : "border-border bg-transparent text-muted-foreground hover:text-foreground hover:border-gold-400"
                  }`}
                  data-testid={`downloads-filter-${slug}`}
                >{label}</button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="text-center text-muted-foreground py-24">Loading library…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-muted-foreground py-24">No matching downloads.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((r) => (
                <a
                  key={`${r.brand_slug}-${r.doc_type}`}
                  href={`${BACKEND_URL}${r.url}`}
                  target="_blank" rel="noopener noreferrer"
                  className="group rounded-2xl bg-muted/30 border border-border p-5 hover:-translate-y-1 hover:border-gold-400 hover:shadow-xl transition-all"
                  data-testid={`download-${r.brand_slug}-${r.doc_type}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="text-3xl">{DOC_ICONS[r.doc_type] || "📄"}</div>
                    <span className="text-[10px] uppercase tracking-widest text-gold-500 font-semibold">PDF</span>
                  </div>
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{r.brand_name}</div>
                    <h3 className="font-display font-bold text-lg mt-1 leading-tight group-hover:text-gold-500 transition-colors">{r.title}</h3>
                    <p className="text-xs text-muted-foreground mt-2">For {r.audience}</p>
                  </div>
                  <div className="mt-5 inline-flex items-center gap-2 text-xs font-semibold text-gold-500">
                    <FileDown className="w-3.5 h-3.5" /> Download PDF
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {legacy.length > 0 && (
        <section className="py-12 bg-muted/20 border-t border-border">
          <div className="max-w-6xl mx-auto px-6">
            <div className="eyebrow text-gold-500 mb-3">Additional resources</div>
            <ul className="divide-y divide-border rounded-2xl border border-border overflow-hidden bg-background">
              {legacy.map((r) => (
                <li key={r.id || r.title} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gold-400/15 flex items-center justify-center">
                      <FileDown className="w-4 h-4 text-gold-500" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{r.title}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{r.category} · {r.type} · {r.size}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
